import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/store/AppStore";
import { formatDate } from "@/utils/format";
import type { AppNotification } from "@/types";

const KIND_META: Record<string, { icon: string; tone: string }> = {
  sale: { icon: "ri-shopping-cart-2-line", tone: "text-accent-300 bg-accent-500/15" },
  order: { icon: "ri-inbox-archive-line", tone: "text-primary-200 bg-primary-500/20" },
  promo: { icon: "ri-megaphone-line", tone: "text-secondary-200 bg-secondary-500/20" },
  info: { icon: "ri-information-line", tone: "text-foreground-700 bg-background-300/40" },
};

const NotificationsCenter = () => {
  const { data, markNotificationRead, markAllRead } = useApp();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Al tocar un aviso lo marca leído y lleva a la pantalla donde se soluciona.
  const handleOpenItem = (n: AppNotification) => {
    markNotificationRead(n.id);
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  const audience = data.role;
  const items = data.notifications.filter(
    (n) => n.audience === audience || n.audience === "all"
  );
  const unread = items.filter((n) => !n.read).length;

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative w-9 h-9 flex items-center justify-center rounded-md border border-accent-500/20 bg-background-200/50 text-foreground-700 hover:text-foreground-950 hover:border-accent-500/40 cursor-pointer"
        aria-label="Notificaciones"
      >
        <i className="ri-notification-3-line text-lg" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-accent-500 text-background-50 text-[10px] font-bold">
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[330px] max-w-[86vw] glass-panel-strong rounded-lg overflow-hidden animate-slide-down z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-accent-500/15">
            <div>
              <p className="text-sm font-semibold text-foreground-950">
                Centro de Notificaciones
              </p>
              <p className="text-[11px] text-foreground-500">
                {audience === "admin" ? "Alertas Master" : "Avisos del Administrador"}
              </p>
            </div>
            {unread > 0 && (
              <button
                onClick={() => markAllRead(audience)}
                className="text-[11px] font-semibold text-accent-300 hover:text-accent-200 cursor-pointer"
              >
                Marcar leídas
              </button>
            )}
          </div>

          <div className="max-h-[320px] overflow-y-auto">
            {items.length === 0 && (
              <div className="px-4 py-10 text-center">
                <span className="mx-auto w-10 h-10 flex items-center justify-center rounded-full bg-background-200/60 text-foreground-500 mb-2">
                  <i className="ri-notification-off-line text-xl" />
                </span>
                <p className="text-xs text-foreground-500">No hay notificaciones por ahora.</p>
              </div>
            )}
            {items.map((n) => {
              const meta = KIND_META[n.kind] ?? KIND_META.info;
              return (
                <button
                  key={n.id}
                  onClick={() => handleOpenItem(n)}
                  className={`w-full text-left flex gap-3 px-4 py-3 border-b border-accent-500/10 hover:bg-background-200/40 cursor-pointer ${
                    n.read ? "opacity-60" : ""
                  }`}
                >
                  <span className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${meta.tone}`}>
                    <i className={`${meta.icon} text-base`} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-foreground-950 truncate">
                        {n.title}
                      </span>
                      {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-accent-400 shrink-0" />}
                    </span>
                    <span className="block text-[11px] text-foreground-500 mt-0.5">
                      {n.message}
                    </span>
                    <span className="block text-[10px] text-foreground-600 mt-1 flex items-center gap-1.5">
                      {formatDate(n.date)}
                      {n.link && (
                        <span className="text-accent-300 font-semibold inline-flex items-center gap-0.5">
                          · Ver <i className="ri-arrow-right-s-line" />
                        </span>
                      )}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsCenter;